import { FC, Suspense, useMemo, useReducer, useState } from 'react';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { getInitialFetchParams } from './__partials/constants/fetch-params';
import { useSelectedCategory } from './__partials/hooks/useSelectedCategory';
import { CasinoGamesConditionalsProvider } from './__partials/providers/CasinoGamesConditionalsProvider';
import {
  CasinoGamesComputedConfigs,
  CasinoGamesConfigs,
  CasinoGamesConfigsProvider
} from './__partials/providers/CasinoGamesConfigsContext';
import { CasinoGamesFetchDisabledProvider } from './__partials/providers/CasinoGamesFetchDisabledContext';
import { CasinoGamesFetchParamsProvider } from './__partials/providers/CasinoGamesFetchParamsContext';
import { casinoGamesConditionalsReducer } from './__partials/state/conditionals-reducer';
import { casinoGamesFetchingParamsReducer } from './__partials/state/reducer';
import {
  computeCasinoGamesArrowsEnabled,
  computeCasinoGamesCategoryNameVisible,
  computeCasinoGamesCountForSliderSkeleton,
  computeCasinoGamesLayoutType,
  computeCasinoGamesLimit,
  computeCasinoGamesLoadMoreButtonEnabled,
  computeCasinoGamesPerRow,
  computeCasinoGamesRows,
  computeCasinoLoadMoreButtonType,
  computeLeftSideBarCasinoAvailable
} from './__partials/utils/compute-configs';
import { CasinoGamesContent } from './GamesContent';
import { useOptions } from '../hooks/useOptions';

const { FetchParamsUpdaterCategory } = lazy(
  () =>
    import(
      /* webpackChunkName: "FetchParamsUpdaterCategory" */ './__partials/fetch-updaters/FetchParamsUpdaterCategory'
    )
);

const { FetchParamsUpdaterProvider } = lazy(
  () =>
    import(
      /* webpackChunkName: "FetchParamsUpdaterProvider" */ './__partials/fetch-updaters/FetchParamsUpdaterProvider'
    )
);

const { FetchParamsUpdaterLimit } = lazy(
  () =>
    import(
      /* webpackChunkName: "FetchParamsUpdaterLimit" */ './__partials/fetch-updaters/FetchParamsUpdaterLimit'
    )
);

const { FetchParamsUpdaterOffset } = lazy(
  () =>
    import(
      /* webpackChunkName: "FetchParamsUpdaterOffset" */ './__partials/fetch-updaters/FetchParamsUpdaterOffset'
    )
);

const { FetchParamsUpdaterByKey } = lazy(
  () =>
    import(
      /* webpackChunkName: "FetchParamsUpdaterByKey" */ './__partials/fetch-updaters/FetchParamsUpdaterByKey'
    )
);

const { FetchParamsUpdaterLastPlayedGames } = lazy(
  () =>
    import(
      /* webpackChunkName: "FetchParamsUpdaterLastPlayedGames" */ './__partials/fetch-updaters/FetchParamsUpdaterLastPlayedGames'
    )
);

const { FetchParamsUpdaterSuggestedGames } = lazy(
  () =>
    import(
      /* webpackChunkName: "FetchParamsUpdaterSuggestedGames" */ './__partials/fetch-updaters/FetchParamsUpdaterSuggestedGames'
    )
);

const { FetchParamsUpdaterFavoriteGames } = lazy(
  () =>
    import(
      /* webpackChunkName: "FetchParamsUpdaterFavoriteGames" */ './__partials/fetch-updaters/FetchParamsUpdaterFavoriteGames'
    )
);

export const CasinoGames: FC<{ configs: Partial<CasinoGamesConfigs> }> = ({
  configs
}) => {
  useOptions({ shouldFetch: configs?.isFirstCasinoElement });
  const { selectedCategory } = useSelectedCategory({ configs });

  const [isFetchDisabled, setIsFetchDisabled] = useState(false);
  // useReducer type issue from react types
  // @ts-ignore
  const [fetchParams, dispatchFetchParams] = useReducer(
    casinoGamesFetchingParamsReducer,
    getInitialFetchParams(configs)
  );

  // useReducer type issue from react types
  // @ts-ignore
  const [conditionals, dispatchConditionals] = useReducer(
    casinoGamesConditionalsReducer,
    {}
  );

  const computedConfigs = useMemo<CasinoGamesComputedConfigs>(() => {
    const loadMoreButtonEnabled = computeCasinoGamesLoadMoreButtonEnabled(
      configs,
      selectedCategory
    );

    const loadMoreButtonType = computeCasinoLoadMoreButtonType(configs);
    const gamesPerRow = computeCasinoGamesPerRow(configs);
    const gamesRows = computeCasinoGamesRows(configs);
    const gamesCountForSkeleton =
      computeCasinoGamesCountForSliderSkeleton(configs);

    const gamesLimit = computeCasinoGamesLimit(configs);
    const arrowsEnabled = computeCasinoGamesArrowsEnabled(configs);
    const leftSideBarAvailable = computeLeftSideBarCasinoAvailable();
    const layoutType = computeCasinoGamesLayoutType(configs);
    const nextSlideOnHover = configs.nextSlideOnHover;
    const gameTitle = configs.gameTitle;

    const categoryNameVisible = computeCasinoGamesCategoryNameVisible(
      configs,
      selectedCategory
    );

    return {
      loadMoreButtonEnabled,
      loadMoreButtonType,
      gamesPerRow,
      gamesRows,
      gamesLimit,
      arrowsEnabled,
      categoryNameVisible,
      leftSideBarAvailable,
      gamesCountForSkeleton,
      layoutType,
      nextSlideOnHover,
      gameTitle
    };
  }, [configs, selectedCategory]);

  const contextProviderConfigsValue = {
    configs,
    computedConfigs
  };

  const contextProviderFetchDisabledValue = {
    isFetchDisabled,
    setIsFetchDisabled
  };

  const contextProviderFetchParamsValue = {
    fetchParams,
    dispatchFetchParams
  };

  return (
    <CasinoGamesConfigsProvider value={contextProviderConfigsValue}>
      <CasinoGamesFetchDisabledProvider
        value={contextProviderFetchDisabledValue}
      >
        <CasinoGamesFetchParamsProvider value={contextProviderFetchParamsValue}>
          <CasinoGamesConditionalsProvider
            value={{ conditionals, dispatchConditionals }}
          >
            {!SpringConfigs.MOCKED_DATA && (
              <Suspense>
                <FetchParamsUpdaterOffset />
                <FetchParamsUpdaterLimit />
                <FetchParamsUpdaterCategory />
                <FetchParamsUpdaterProvider />
                <FetchParamsUpdaterByKey />
                <FetchParamsUpdaterLastPlayedGames />
                <FetchParamsUpdaterSuggestedGames />
                <FetchParamsUpdaterFavoriteGames />
              </Suspense>
            )}
            <CasinoGamesContent />
          </CasinoGamesConditionalsProvider>
        </CasinoGamesFetchParamsProvider>
      </CasinoGamesFetchDisabledProvider>
    </CasinoGamesConfigsProvider>
  );
};
